import { apiClient } from '@/api/client';
import { useAuth } from '@/lib/auth';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { useDebounce } from './useDebounce';

export interface PaginatedListConfig<TFilters extends Record<string, string>> {
  queryKey: string;
  endpoint: string;
  initialFilters: TFilters;
  defaultSort?: { sortBy: string; sortOrder: 'asc' | 'desc' };
  limit?: number;
  buildQueryParams?: (params: {
    page: number;
    limit: number;
    search: string;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    filters: TFilters;
  }) => Record<string, string | number | boolean | undefined>;
}

export interface PaginatedListState<TFilters> {
  page: number;
  search: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  filters: TFilters;
}

export interface PaginatedList<TItem> {
  items: TItem[];
  total: number;
  totalPages: number;
  currentPage: number;
  limit: number;
}

export interface PaginatedListActions<TFilters> {
  setPage: (page: number) => void;
  setSearch: (value: string) => void;
  setSort: (key: string) => void;
  setFilter: <K extends keyof TFilters>(key: K, value: TFilters[K]) => void;
  clearFilters: () => void;
  refetch: () => void;
}

export interface UsePaginatedListReturn<TItem, TFilters> {
  state: PaginatedListState<TFilters>;
  list: PaginatedList<TItem>;
  actions: PaginatedListActions<TFilters>;
  isLoading: boolean;
}

const defaultBuildQueryParams = <
  TFilters extends Record<string, string>,
>(params: {
  page: number;
  limit: number;
  search: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  filters: TFilters;
}): Record<string, string | number | boolean | undefined> => {
  const query: Record<string, string | number | boolean | undefined> = {
    page: params.page,
    limit: params.limit,
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
  };

  if (params.search) {
    query.search = params.search;
  }

  Object.entries(params.filters).forEach(([key, value]) => {
    if (value === '' || value === undefined || value === null) return;

    // HTML form values are strings, convert to boolean for the API
    if (value === 'true') {
      query[key] = true;
    } else if (value === 'false') {
      query[key] = false;
    } else {
      query[key] = value;
    }
  });

  return query;
};

const extractPaginationFromHeaders = (headers: Headers) => ({
  total: parseInt(headers.get('X-Total-Count') || '0'),
  totalPages: parseInt(headers.get('X-Total-Pages') || '1'),
  currentPage: parseInt(headers.get('X-Current-Page') || '1'),
  limit: parseInt(headers.get('X-Per-Page') || '10'),
});

export const usePaginatedList = <
  TItem,
  TFilters extends Record<string, string>,
>(
  config: PaginatedListConfig<TFilters>,
): UsePaginatedListReturn<TItem, TFilters> => {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const {
    queryKey,
    endpoint,
    initialFilters,
    defaultSort = { sortBy: 'createdAt', sortOrder: 'desc' },
    limit = 10,
    buildQueryParams = defaultBuildQueryParams,
  } = config;

  const [page, setPage] = React.useState(1);
  const [search, setSearchRaw] = React.useState('');
  const [sortBy, setSortBy] = React.useState(defaultSort.sortBy);
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>(
    defaultSort.sortOrder,
  );
  const [filters, setFilters] = React.useState<TFilters>(initialFilters);

  const debouncedSearch = useDebounce(search, 300);

  const {
    data: queryData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [
      queryKey,
      { page, search: debouncedSearch, sortBy, sortOrder, ...filters },
    ],
    queryFn: async () => {
      const queryParams = buildQueryParams({
        page,
        limit,
        search: debouncedSearch,
        sortBy,
        sortOrder,
        filters,
      });

      const fetchEndpoint = apiClient.GET as (
        url: string,
        init: {
          params: {
            query: Record<string, string | number | boolean | undefined>;
          };
        },
      ) => Promise<{
        data?: unknown;
        error?: unknown;
        response: Response;
      }>;

      const response = await fetchEndpoint(endpoint, {
        params: { query: queryParams },
      });

      if (response.error) {
        throw new Error(`Failed to fetch ${queryKey}`);
      }

      const pagination = extractPaginationFromHeaders(
        response.response.headers,
      );

      return {
        items: (response.data || []) as TItem[],
        ...pagination,
      };
    },
    enabled: isAuthenticated && !isAuthLoading,
  });

  const setSearch = React.useCallback((value: string) => {
    setSearchRaw(value);
    setPage(1);
  }, []);

  const setSort = React.useCallback(
    (key: string) => {
      if (sortBy === key) {
        setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(key);
        setSortOrder('asc');
      }
      setPage(1);
    },
    [sortBy],
  );

  const setFilter = React.useCallback(
    <K extends keyof TFilters>(key: K, value: TFilters[K]) => {
      setFilters(prev => ({ ...prev, [key]: value }));
      setPage(1);
    },
    [],
  );

  const clearFilters = React.useCallback(() => {
    setSearchRaw('');
    setFilters(initialFilters);
    setPage(1);
    setSortBy(defaultSort.sortBy);
    setSortOrder(defaultSort.sortOrder);
  }, [initialFilters, defaultSort.sortBy, defaultSort.sortOrder]);

  const state: PaginatedListState<TFilters> = {
    page,
    search,
    sortBy,
    sortOrder,
    filters,
  };

  const list: PaginatedList<TItem> = {
    items: queryData?.items || [],
    total: queryData?.total || 0,
    totalPages: queryData?.totalPages || 1,
    currentPage: queryData?.currentPage || 1,
    limit: queryData?.limit || limit,
  };

  const actions: PaginatedListActions<TFilters> = {
    setPage,
    setSearch,
    setSort,
    setFilter,
    clearFilters,
    refetch,
  };

  return {
    state,
    list,
    actions,
    isLoading,
  };
};
